/**
 * P-305 · the column-resize **gesture**, in one place.
 *
 * The board grid has owned this gesture since B4 (`modules/board-view/table/ColumnHeader.tsx`'s
 * `startResize`): pointer capture on the gutter, window-level `pointermove` / `pointerup`, a
 * **live preview** that paints and never writes, and a commit on pointer-up only — and only when
 * the number actually moved. The Tasks list needs the same gesture on its own header, and a
 * second copy of it is how two surfaces end up disagreeing about what a drag does.
 *
 * So it lives here, in `lib/`, generic over the key a surface calls its columns by and free of
 * any knowledge of widths, storage or CSS. The grid's copy is untouched on purpose: round 11i is
 * inside `board-view/**` while this round runs, and ripping the gesture out from under it would
 * be a merge conflict in the one file two rounds share. `ColumnHeader.tsx` is to adopt this hook
 * the moment that file is free — the behaviour below is its behaviour, line for line.
 *
 * What a caller supplies:
 *
 *   `clamp`     the floor/ceiling for **this** column — the grid's `clampWidth(next, minWidth)`,
 *               which is per column because the name column's floor is not an ordinary one;
 *   `onCommit`  the write. It runs once, on pointer-up, with the clamped number.
 *
 * What it hands back is the **active** drag — `{ key, width }` or `null` — which is the live
 * preview. The caller paints it; nothing here touches the DOM beyond the pointer capture, so a
 * surface that keeps its widths in CSS custom properties (Tasks) and one that writes an inline
 * `width` (the grid) can share the same gesture.
 */
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

/** The drag in flight: which column, and the width it would commit if it ended now. */
export interface ColumnResizeState<K extends string> {
  key: K;
  width: number;
}

export interface UseColumnResizeOptions<K extends string> {
  /** Per column, because a floor is a property of the column and not of the gesture. */
  clamp(key: K, width: number): number;
  /** Runs once, on pointer-up, and only when the width actually changed. */
  onCommit(key: K, width: number): void;
}

export interface ColumnResizeHandle<K extends string> {
  /** The live preview, or `null` when no drag is in flight. */
  active: ColumnResizeState<K> | null;
  /**
   * Starts a drag from `startWidth` — the width the column is **painted** at, which is the only
   * honest starting point for a column whose box comes out of a flex line rather than out of a
   * stored number.
   */
  start(event: ReactPointerEvent<HTMLElement>, key: K, startWidth: number): void;
}

export function useColumnResize<K extends string>({
  clamp,
  onCommit,
}: UseColumnResizeOptions<K>): ColumnResizeHandle<K> {
  const [active, setActive] = useState<ColumnResizeState<K> | null>(null);
  /*
   * The preview is held in a ref as well as in state: `onUp` fires from a window listener whose
   * closure was made when the drag started, so reading the committed number off `active` would
   * read the value that state had at `pointerdown` — i.e. the width nobody dragged to.
   */
  const preview = useRef<ColumnResizeState<K> | null>(null);
  /** Everything the in-flight listeners need to be torn down from anywhere. */
  const teardown = useRef<(() => void) | null>(null);

  /* A drag that is still in flight when the surface unmounts must not leave listeners behind. */
  useEffect(() => () => teardown.current?.(), []);

  const start = useCallback(
    (event: ReactPointerEvent<HTMLElement>, key: K, startWidth: number) => {
      event.preventDefault();
      event.stopPropagation();
      /* A second pointer landing mid-drag would otherwise install a second pair of listeners. */
      teardown.current?.();

      const startX = event.clientX;
      const target = event.currentTarget;
      const pointerId = event.pointerId;
      target.setPointerCapture(pointerId);

      const onMove = (move: PointerEvent) => {
        const width = clamp(key, startWidth + (move.clientX - startX));
        const next = { key, width };
        preview.current = next;
        setActive(next);
      };
      const onUp = () => {
        teardown.current?.();
        const committed = preview.current;
        preview.current = null;
        setActive(null);
        // Commit on pointer-up only — the live preview never writes (§8.2), and a drag that
        // ended where it started writes nothing at all.
        if (committed !== null && committed.width !== clamp(key, startWidth)) {
          onCommit(committed.key, committed.width);
        }
      };

      teardown.current = () => {
        teardown.current = null;
        try {
          target.releasePointerCapture(pointerId);
        } catch {
          // The node may already be gone (an unmount mid-drag); releasing then throws and the
          // capture is released with it anyway.
        }
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      /* A cancelled pointer (a system gesture, a lost capture) ends the drag like a pointer-up. */
      window.addEventListener('pointercancel', onUp);
    },
    [clamp, onCommit],
  );

  return { active, start };
}
